import { EventEmitter, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Config, Driver, driver, DriveStep, State } from "driver.js";
import { BehaviorSubject, map, Observable, take } from "rxjs";
import { waitForElement } from "src/app/common/tools/domUtils";

export interface FeatureTour {
  id: string;
  title: string;
  description: string;
  startPage?: string;
  steps: (DriveStep & {
    advanceOn?: string | { event: string; selector: string };
    waitFor?: string | Element;
  })[];
  version: string;
  priority?: number;
  disableBackButton?: boolean;
}

/** Centralised service that manages application feature‑tours powered by driver.js. */
@Injectable({ providedIn: "root" })
export class FeatureTourService {
  private static readonly STORAGE_KEY_PREFIX = "featureTour_";
  private static readonly STORAGE_KEY_VERSIONS = "featureTourVersions";

  // Internationalized button labels
  private readonly nextButtonText = $localize`:@@feature_tour_next_button:Suivant`;
  private readonly prevButtonText = $localize`:@@feature_tour_prev_button:Précédent`;
  private readonly doneButtonText = $localize`:@@feature_tour_done_button:Terminer`;

  private readonly toursSubject = new BehaviorSubject<FeatureTour[]>([]);
  /** Emits _true_ whenever at least one tour hasn’t been viewed yet */
  private readonly newToursAvailableSubject = new BehaviorSubject<boolean>(false);

  private readonly driver: Driver = this.createDriver();
  private activeTourId: string | null = null;

  private readonly tourStarted = new EventEmitter<string>();
  private readonly tourEnded = new EventEmitter<string>();
  private readonly router = inject(Router);

  constructor() {
    this.updateNewToursFlag();
  }

  getTourStartedEvents(): Observable<string> {
    return this.tourStarted.asObservable();
  }

  getTourEndedEvents(): Observable<string> {
    return this.tourEnded.asObservable();
  }

  getAvailableTours(): Observable<FeatureTour[]> {
    return this.toursSubject.asObservable();
  }

  getNewToursAvailable(): Observable<boolean> {
    return this.newToursAvailableSubject.asObservable();
  }

  addTour(tour: FeatureTour): void {
    this.registerTour(tour);
  }

  registerToursFromModule(toursModule: Record<string, unknown>): void {
    Object.values(toursModule).forEach((tour) => {
      tour = typeof tour === "function" ? (tour as (router?: Router) => FeatureTour)(this.router) : tour;
      if (this.isFeatureTour(tour)) {
        this.registerTour(tour);
      }
    });
  }

  showTour(tourId: string): void {
    this.startTour(tourId);
  }

  hasTourBeenViewed(tourId: string): boolean {
    return this.isTourViewed(tourId);
  }

  acknowledgeNewTours(): void {
    this.newToursAvailableSubject.next(false);
  }

  getAllUnviewedTours(): Observable<FeatureTour[]> {
    return this.toursSubject.pipe(map((tours) => tours.filter((t) => !this.isTourViewed(t.id))));
  }

  private createDriver(): Driver {
    return driver({
      showProgress: true,
      allowClose: false,
      disableActiveInteraction: false,
      showButtons: ["next", "close"],
      nextBtnText: this.nextButtonText,
      prevBtnText: this.prevButtonText,
      doneBtnText: this.doneButtonText,
      onHighlightStarted: (
        element: Element | undefined,
        step: DriveStep,
        options: { driver: Driver; config: Config; state: State },
      ): void => {
        // Handle advanceOn property
        const customStep = step as DriveStep & {
          advanceOn?: string | { event: string; selector: string };
        };

        if (customStep.advanceOn && element) {
          const eventHandler = (event: Event): void => {
            const originalOnNextClick = step.popover?.onNextClick;

            if (options.driver.hasNextStep() && !originalOnNextClick) {
              options.driver.moveNext();
            } else if (originalOnNextClick) {
              originalOnNextClick(element, step, options);
            }
          };

          let targetElement: Element | null = element;
          let eventName = "";

          // Check if advanceOn is a string or an object
          if (typeof customStep.advanceOn === "string") {
            eventName = customStep.advanceOn;
          } else {
            // advanceOn is an object with event and selector properties
            eventName = customStep.advanceOn.event;
            // Find the element by selector
            targetElement = document.querySelector(customStep.advanceOn.selector);
            if (!targetElement) {
              console.error(`Element with selector "${customStep.advanceOn.selector}" not found.`);
              return;
            }
          }

          // Add event listener to the target element
          targetElement.addEventListener(eventName, eventHandler);

          // Override the step's onNextClick to clean up event listeners
          if (step.popover) {
            const originalOnNextClick = step.popover.onNextClick;
            step.popover.onNextClick = (
              el: Element | undefined,
              stp: DriveStep,
              opts: { driver: Driver; state: State; config: Config },
            ): void => {
              // Remove the event listener
              if (targetElement) {
                targetElement.removeEventListener(eventName, eventHandler);
              }

              if (originalOnNextClick) {
                originalOnNextClick(el, stp, opts);
              }
            };
          }
        }
      },
      onDestroyStarted: () => {
        if (this.activeTourId) {
          this.tourEnded.emit(this.activeTourId);
          this.markTourAsViewed(this.activeTourId);
          this.activeTourId = null;
        }
        this.driver.destroy();
      },
    });
  }

  private registerTour(tour: FeatureTour): void {
    this.toursSubject.pipe(take(1)).subscribe((currentTours) => {
      const index = currentTours.findIndex((t) => t.id === tour.id);
      const updatedTours =
        index === -1 ? [...currentTours, tour] : currentTours.map((t) => (t.id === tour.id ? tour : t));
      updatedTours.sort((a, b) => {
        const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
        const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
        return pa - pb;
      });
      this.toursSubject.next(updatedTours);
      this.updateTourVersion(tour.id, tour.version);
      this.updateNewToursFlag();
    });
  }

  private startTour(tourId: string): void {
    this.toursSubject
      .pipe(
        take(1),
        map((tours) => tours.find((t) => t.id === tourId)),
      )
      .subscribe((tour) => {
        if (!tour) {
          console.error(`Feature‑tour “${tourId}” not found.`);
          return;
        }

        try {
          // Check if we need to navigate to a specific page before starting the tour
          if (tour.startPage && this.router.url !== tour.startPage) {
            // Navigate to the start page and then start the tour
            this.router
              .navigateByUrl(tour.startPage)
              // Wait for the first step element to be available
              .then(async () => {
                if (tour.steps[0]?.element) {
                  await waitForElement(tour.steps[0].waitFor || tour.steps[0].element, 5000, 100);
                }
                return void 0;
              })
              .then(() => {
                // Start the tour after navigation is complete
                this.activeTourId = tourId;
                this.driver.setSteps(tour.steps);
                this.driver.drive();
                this.tourStarted.emit(tourId);
              })
              .catch((error: unknown) => {
                console.error("Error navigating to start page:", error);
                this.activeTourId = null;
              });
          } else {
            // Start the tour immediately if we're already on the correct page
            this.activeTourId = tourId;
            this.driver.setSteps(tour.steps);
            this.driver.drive();
            this.tourStarted.emit(tourId);
          }
        } catch (err) {
          console.error("Error starting tour", err);
          this.activeTourId = null;
        }
      });
  }

  private isTourViewed(tourId: string): boolean {
    return localStorage.getItem(`${FeatureTourService.STORAGE_KEY_PREFIX}${tourId}`) === "true";
  }

  private markTourAsViewed(tourId: string): void {
    localStorage.setItem(`${FeatureTourService.STORAGE_KEY_PREFIX}${tourId}`, "true");
    this.updateNewToursFlag();
  }

  private updateNewToursFlag(): void {
    this.toursSubject.pipe(take(1)).subscribe((tours) => {
      const hasUnviewed = tours.some((t) => !this.isTourViewed(t.id));
      this.newToursAvailableSubject.next(hasUnviewed);
    });
  }

  private updateTourVersion(tourId: string, version: string): void {
    const versions = this.getStoredVersions();

    if (versions[tourId] !== version) {
      localStorage.removeItem(`${FeatureTourService.STORAGE_KEY_PREFIX}${tourId}`);
    }

    versions[tourId] = version;
    this.saveStoredVersions(versions);
  }

  private getStoredVersions(): Record<string, string> {
    return JSON.parse(localStorage.getItem(FeatureTourService.STORAGE_KEY_VERSIONS) ?? "{}") as Record<string, string>;
  }

  private saveStoredVersions(versions: Record<string, string>): void {
    localStorage.setItem(FeatureTourService.STORAGE_KEY_VERSIONS, JSON.stringify(versions));
  }

  private isFeatureTour(obj: unknown): obj is FeatureTour {
    if (typeof obj !== "object" || obj === null) {
      return false;
    }

    const maybe = obj as Partial<FeatureTour>;

    return (
      typeof maybe.id === "string"
      && typeof maybe.title === "string"
      && typeof maybe.description === "string"
      && Array.isArray(maybe.steps)
      && typeof maybe.version === "string"
    );
  }
}
